/*
 * The Notifications sheet's one list: what's coming due (from `coming-up`)
 * and what people did (mentions, activity, assignments, intakes and company
 * requests from the inbox), grouped by when, filtered by kind. Inbox items are always yours, so Team shows due
 * items only.
 */
import dayjs from "dayjs";

import type { ComingUpItem, ComingUpKind, ComingUpScope } from "./comingUp/api";
import { isUnread, type Notification } from "./types";

export type InboxKind =
  | "mention"
  | "activity"
  | "assignment"
  | "intake"
  | "company";
export type FeedKind = ComingUpKind | InboxKind;
export type FeedGroup = "overdue" | "today" | "week" | "later" | "earlier";

export const FEED_GROUPS: [FeedGroup, string][] = [
  ["overdue", "Overdue"],
  ["today", "Today"],
  ["week", "Next 7 Days"],
  ["later", "Later"],
  ["earlier", "Earlier"]
];

export const COMING_DUE_KINDS: ComingUpKind[] = [
  "filing",
  "expiration",
  "appointment",
  "reminder"
];
export const FROM_PEOPLE_KINDS: InboxKind[] = [
  "mention",
  "activity",
  "assignment",
  "intake",
  "company"
];

export const FEED_KIND_LABELS: Record<FeedKind, string> = {
  filing: "Filings",
  expiration: "Expirations",
  appointment: "Appointments",
  reminder: "Reminders",
  mention: "Mentions",
  activity: "Activity",
  assignment: "Assignments",
  intake: "Intakes",
  company: "Companies"
};

const INBOX_TYPES: Record<string, InboxKind> = {
  "case_agent.mention": "mention",
  "form.comment_mention": "mention",
  "form.comment_reply": "mention",
  "client.activity": "activity",
  "agent.cross_scope_write": "activity",
  "time.evening_review": "activity",
  "time.unreleased_day": "activity",
  "time.returned": "activity",
  "time.nudge": "activity",
  "key_facts.update": "activity",
  "client.assigned": "assignment",
  "case.assigned": "assignment",
  "intake.submitted": "intake",
  "company.request": "company",
  "company.message": "company",
  "company.approval_answered": "company"
};

/** The sheet's kind for a notification, or null when the sheet doesn't show it. */
export const inboxKind = (n: Pick<Notification, "type">): InboxKind | null =>
  INBOX_TYPES[n.type] ?? null;

const day = (d: dayjs.Dayjs) => d.format("YYYY-MM-DD");

export const dueGroup = (
  item: Pick<ComingUpItem, "due_date" | "overdue">,
  now = dayjs()
): FeedGroup => {
  if (item.overdue) return "overdue";
  const due = item.due_date ?? "";
  const today = day(now);
  if (!due || due <= today) return "today";
  if (due <= day(now.add(7, "day"))) return "week";
  return "later";
};

export const inboxGroup = (
  n: Pick<Notification, "created_at">,
  now = dayjs()
): FeedGroup =>
  n.created_at && day(dayjs(n.created_at)) === day(now) ? "today" : "earlier";

/** The due pill: the date, or Today / Tomorrow. */
export const duePill = (
  item: Pick<ComingUpItem, "due_date">,
  now = dayjs()
): string => {
  if (!item.due_date) return "";
  const d = dayjs(item.due_date);
  if (day(d) === day(now)) return "Today";
  if (day(d) === day(now.add(1, "day"))) return "Tomorrow";
  return d.year() === now.year() ? d.format("MMM D") : d.format("MMM D, YYYY");
};

export type FeedRow =
  | { kind: "due"; item: ComingUpItem }
  | { kind: "inbox"; item: Notification; inbox: InboxKind };

export type FeedSection = { group: FeedGroup; label: string; rows: FeedRow[] };

export const shownKind = (selection: FeedKind[], kind: FeedKind) =>
  !selection.length || selection.includes(kind);

/** Kinds the selection can hold in a scope: Team has no inbox kinds. */
export const scopeKinds = (scope: ComingUpScope): FeedKind[] =>
  scope === "team"
    ? [...COMING_DUE_KINDS]
    : [...COMING_DUE_KINDS, ...FROM_PEOPLE_KINDS];

export const scopedSelection = (
  selection: FeedKind[],
  scope: ComingUpScope
): FeedKind[] => {
  const allowed = new Set(scopeKinds(scope));
  return selection.filter((k) => allowed.has(k));
};

export function buildFeed({
  due,
  inbox,
  scope,
  selection,
  now = dayjs()
}: {
  due: ComingUpItem[];
  inbox: Notification[];
  scope: ComingUpScope;
  selection: FeedKind[];
  now?: dayjs.Dayjs;
}): FeedSection[] {
  const sel = scopedSelection(selection, scope);
  const groups = new Map<FeedGroup, FeedRow[]>(
    FEED_GROUPS.map(([g]) => [g, []])
  );
  due
    .filter((item) => shownKind(sel, item.kind))
    .forEach((item) =>
      groups.get(dueGroup(item, now))?.push({ kind: "due", item })
    );
  if (scope === "mine") {
    inbox.forEach((n) => {
      const kind = inboxKind(n);
      if (!kind || !shownKind(sel, kind)) return;
      groups
        .get(inboxGroup(n, now))
        ?.push({ kind: "inbox", item: n, inbox: kind });
    });
  }
  return FEED_GROUPS.map(([group, label]) => ({
    group,
    label,
    rows: groups.get(group) ?? []
  })).filter((s) => s.rows.length > 0);
}

export const kindCounts = (
  due: ComingUpItem[],
  inbox: Notification[],
  scope: ComingUpScope
): Record<FeedKind, number> => {
  const counts: Record<FeedKind, number> = {
    filing: 0,
    expiration: 0,
    appointment: 0,
    reminder: 0,
    mention: 0,
    activity: 0,
    assignment: 0,
    intake: 0,
    company: 0
  };
  for (const item of due) counts[item.kind] += 1;
  if (scope === "mine")
    for (const n of inbox) {
      const kind = inboxKind(n);
      if (kind) counts[kind] += 1;
    }
  return counts;
};

export const filterLabel = (selection: FeedKind[]): string =>
  !selection.length
    ? "Filter"
    : selection.length === 1
    ? FEED_KIND_LABELS[selection[0]]
    : `${selection.length} Kinds`;

/**
 * The bell's count: my items of any kind that are overdue or due today, plus
 * unread mentions, assignments and intakes. Client activity never adds to it.
 */
export const bellCount = (
  mine: ComingUpItem[],
  inbox: Notification[],
  now = dayjs()
): number => {
  const due = mine.filter((i) => {
    const g = dueGroup(i, now);
    return g === "overdue" || g === "today";
  }).length;
  const forMe = inbox.filter((n) => {
    const kind = inboxKind(n);
    return kind !== null && kind !== "activity" && isUnread(n);
  }).length;
  return due + forMe;
};

const SELECTION_KEY = (uid: string) => `NOTIFICATIONS_FILTER_v1_${uid}`;
const ALL_KINDS = new Set<string>([...COMING_DUE_KINDS, ...FROM_PEOPLE_KINDS]);

export const loadSelection = (uid: string | null | undefined): FeedKind[] => {
  if (!uid) return [];
  try {
    const raw: unknown = JSON.parse(
      localStorage.getItem(SELECTION_KEY(uid)) ?? "[]"
    );
    return Array.isArray(raw)
      ? (raw.filter(
          (k) => typeof k === "string" && ALL_KINDS.has(k)
        ) as FeedKind[])
      : [];
  } catch {
    return [];
  }
};

export const saveSelection = (
  uid: string | null | undefined,
  selection: FeedKind[]
) => {
  if (!uid) return;
  try {
    localStorage.setItem(SELECTION_KEY(uid), JSON.stringify(selection));
  } catch {
    // Private mode: the filter lasts for this page only.
  }
};
